// src/components/layout/Header.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

// Конфигурация меню для разных ролей
const menuConfig = [
  {
    label: 'Главная',
    path: '/',
    groupKey: 'home',
    roles: { required: [], optional: ['analytics', 'qc', 'qc_customer'] },
    type: 'base'
  },
  {
    label: 'Новости',
    path: '/news',
    groupKey: 'news',
    roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
    type: 'base'
  },
  {
    label: 'Звонки',
    path: '/sounds',
    groupKey: 'sounds',
    roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
    type: 'base'
  },
  {
    label: 'Аналитика РА',
    path: null,
    groupKey: 'analytics-ra',
    roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
    type: 'dropdown',
    children: [
      {
        label: 'Статистика проверки ИИ',
        path: '/analytics',
        roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
      },
      {
        label: 'Управление аналитикой',
        path: '/admin-analytics',
        roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
      },
      {
        label: 'Статус Аналитики',
        path: '/status-analytics',
        roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
      },
      {
        label: 'Аналитика автооценок',
        path: '/analytics/stats',
        roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
      },
      {
        label: 'Аналитика РА',
        path: '/analytics-ra',
        roles: { required: ['analytics'], optional: ['admin', 'superuser'] },
      }
    ]
  },
  {
    label: 'КК',
    path: null,
    groupKey: 'kk',
    roles: { required: ['qc'], optional: ['admin', 'superuser', 'operator', 'auditor', 'customer'] },
    type: 'dropdown',
    children: [
      {
        label: 'Оценка',
        path: '/quality-assessment',
        roles: { required: ['qc'], optional: ['admin', 'superuser', 'operator', 'auditor'] },
      },
      {
        label: 'Аналитика КК',
        path: '/analytics-kk',
        roles: { required: ['qc'], optional: ['admin', 'superuser', 'auditor', 'customer' ] },
      },
      {
        label: 'Настройка КК',
        path: '/quality-settings',
        roles: { required: ['qc'], optional: ['admin', 'superuser'] },
      },
      {
        label: 'Ответы для формы КК',
        path: '/quality-assessment-options',
        roles: { required: ['qc'], optional: ['admin', 'superuser'] },
      }
    ]
  },
  {
    label: 'Пользователи',
    path: null,
    groupKey: 'users',
    roles: { required: ['analytics', 'qc'], optional: ['admin', 'superuser', 'operator', 'auditor', 'customer'] },
    type: 'dropdown',
    children: [
      {
        label: 'Профиль',
        path: (user) => `/user/${user.id}`,
        roles: { required: ['analytics', 'qc'], optional: ['admin', 'superuser', 'operator', 'auditor', 'customer'] },
      },
      {
        label: 'Управление пользователями',
        path: '/users',
        roles: { required: ['analytics', 'qc'], optional: ['admin', 'superuser'] },
      }
    ]
  },
  {
    label: 'Проекты',
    path: null,
    groupKey: 'projects',
    roles: { required: ['analytics', 'qc'], optional: ['superuser'] },
    type: 'dropdown',
    children: [
      {
        label: 'Настройка проектов',
        path: '/projects',
        roles: { required: ['analytics', 'qc'], optional: ['superuser'] },
      }
    ]
  },
  {
    label: 'Выйти',
    path: '/logout',
    groupKey: 'logout',
    roles: { required: [], optional: [] },
    type: 'base'
  }
];

// Проверка доступа к кнопке на основе ролей
const checkRoleAccess = (roleConfig, roleNames, isSuperuser) => {
  if (!roleConfig) return false;
  
  // Если есть только superuser в required — доступ только для superuser
  if (roleConfig.required && roleConfig.required.length === 1 && roleConfig.required[0] === 'superuser') {
    return isSuperuser;
  }
  
  // Проверяем обязательные роли — все должны быть
  if (roleConfig.required && roleConfig.required.length > 0) {
    const hasRequired = roleConfig.required.every(r => roleNames.has(r));
    if (!hasRequired) {
      return false;
    }
  }
  
  // Проверяем опциональные роли — хотя бы одна должна быть (если есть опциональные)
  // superuser проверяется отдельно через isSuperuser
  if (roleConfig.optional && roleConfig.optional.length > 0) {
    // Исключаем superuser из проверки, так как он проверяется отдельно
    const optionalRoles = roleConfig.optional.filter(r => r !== 'superuser');
    
    // Проверяем, есть ли у пользователя хотя бы одна опциональная роль
    const hasOptional = optionalRoles.some(r => roleNames.has(r));
    
    // Проверяем, является ли пользователь superuser
    const isSuperuserInOptional = roleConfig.optional.includes('superuser') && isSuperuser;
    
    if (!hasOptional && !isSuperuserInOptional) {
      return false;
    }
  }
  
  return true;
};

// Генерация десктопного меню из конфигурации
const generateDesktopNav = (roleNames, isSuperuser, user) => {
  const navItems = [];
  menuConfig.forEach(buttonItem => {
    if (buttonItem.type === 'base') {
      if (checkRoleAccess(buttonItem.roles, roleNames, isSuperuser)) {
        navItems.push(
          <Link key={buttonItem.path} to={typeof buttonItem.path === 'function' ? buttonItem.path(user) : buttonItem.path} className="nav-btn">
            {buttonItem.label}
          </Link>
        );
      }
    } else if (buttonItem.type === 'dropdown') {
      const groupKey = buttonItem.groupKey;
      if (groupKey === undefined) return;
      if (checkRoleAccess(buttonItem.roles, roleNames, isSuperuser)) {
        navItems.push(
          <div key={groupKey} className="dropdown">
            <button className="nav-btn">{buttonItem.label} ▼</button>
            <div className="dropdown-menu">
              {buttonItem.children.map(item => (
                checkRoleAccess(item.roles, roleNames, isSuperuser) && (
                  <Link key={item.path} to={typeof item.path === 'function' ? item.path(user) : item.path} className="dropdown-item">
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        );
      }
    }
  });

  return navItems;
};

const generateMobileNav = (roleNames, isSuperuser, user, setIsMenuOpen) => {
  const navMobileItems = [];

  menuConfig.forEach(buttonItem => {
    if (buttonItem.type === 'base') {
      if (checkRoleAccess(buttonItem.roles, roleNames, isSuperuser)) {
        navMobileItems.push(
          <Link
            key={buttonItem.path}
            to={typeof buttonItem.path === 'function' ? buttonItem.path(user) : buttonItem.path}
            className="dropdown-item py-2"
            onClick={() => setIsMenuOpen(false)}
          >
            {buttonItem.label}
          </Link>
        );
      }
    } else if (buttonItem.type === 'dropdown') {
      const groupKey = buttonItem.groupKey;
      if (groupKey === undefined) return;
      if (checkRoleAccess(buttonItem.roles, roleNames, isSuperuser)) {
        navMobileItems.push(
          <div key={groupKey} className="border-top pt-2 mt-2">
            <h6 className="text-muted small px-2 mb-2">{buttonItem.label}</h6>
            {buttonItem.children.map(item => (
              checkRoleAccess(item.roles, roleNames, isSuperuser) && (
                <Link
                  key={item.path}
                  to={typeof item.path === 'function' ? item.path(user) : item.path}
                  className="dropdown-item py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>
        );
      }
    }
  });

  return navMobileItems;
};

const Header = ({ setIsMenuOpen }) => {
  const { user, roleNames, isAuthenticated, isSuperuser } = useAuth();
  const navItems = generateDesktopNav(roleNames, isSuperuser, user);
  const navMobileItems = generateMobileNav(roleNames, isSuperuser, user, setIsMenuOpen);

  return (
    <>
      <header>
        <div className="nav-logo">
          <img src="/images/logo.png" alt="Логотип"/>
          <span>Речевая аналитика</span>
        </div>

        <div className="nav-buttons d-none d-xl-flex">
          {navItems}
        </div>
          <button className="btn btn-secondary-dark d-xl-none" type="button" data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasWithBothOptions" aria-controls="offcanvasWithBothOptions">
            ☰ Меню
          </button>

        <div className="offcanvas offcanvas-start" data-bs-scroll="true" tabIndex="-1" id="offcanvasWithBothOptions"
             aria-labelledby="offcanvasWithBothOptionsLabel">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="offcanvasWithBothOptionsLabel">Меню</h5>
            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Закрыть"></button>
          </div>
          <div className="offcanvas-body">
            {navMobileItems}
          </div>
        </div>

      </header>
    </>
  );
};

export default Header;
